/**
 * A07（2026-11-03 和田様の UAT）。車両ナンバーを地名・分類番号・ひらがな・一連番号に分けて入れる。
 *
 * 保存は今の plate_number（1つの文字列）のまま。4つの欄から「品川 330 な 38-59」の形
 * （半角の空白で区切る）に組み立てて送る。形をそろえると、ナンバーの並べ替えで地名・分類番号ごとに
 * まとまり、「38-59」のような一連番号でも検索できる。
 *
 * 既存の値は分けられる形なら4つの欄に分けて出す。分けられない値（自由入力の頃の値）は、
 * 欄を書き換えるまで元の文字列のまま残す（表示も保存も壊さない）。
 */

export interface PlateParts {
  /** 地名（例 品川・なにわ・尾張小牧）。 */
  area: string;
  /** 分類番号（1〜3桁。英字を含むことがある。例 330・30A）。 */
  classNumber: string;
  /** ひらがな1文字（例 な）。 */
  kana: string;
  /** 一連番号（1〜4桁の数字。欄では数字だけ。例 3859・12）。 */
  serial: string;
}

export const EMPTY_PLATE: PlateParts = { area: '', classNumber: '', kana: '', serial: '' };

// 全角の数字・英字を半角に。
const toHalfWidth = (text: string) =>
  text.replace(/[０-９Ａ-Ｚａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));

/** 一連番号の数字（全角は半角に。ハイフン・中黒・空白は読み飛ばす）。 */
export function serialDigits(serial: string): string {
  return toHalfWidth(serial).replace(/\D/g, '');
}

/**
 * 一連番号が4桁を超えるか。#475 の自動レビュー（P2）。以前は5桁目以降を黙って切り捨て、
 * 画面に「12345」と出たまま「12-34」で保存していた。入力の誤りとして保存を止める。
 */
export function isSerialTooLong(serial: string): boolean {
  return serialDigits(serial).length > 4;
}

/**
 * 一連番号を表記の形にする。4桁は「38-59」、3桁以下は先頭を「・」で埋める（「・・12」）。
 * 数字以外（ハイフン・中黒・空白）は読み飛ばす。数字が無い・4桁を超えるときは空（保存させない）。
 */
export function formatSerial(serial: string): string {
  const digits = serialDigits(serial);
  if (!digits || digits.length > 4) return '';
  if (digits.length === 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${'・'.repeat(4 - digits.length)}${digits}`;
}

/** 分類番号を半角・大文字にする（1〜3文字の数字と英字）。 */
export function formatClassNumber(classNumber: string): string {
  return toHalfWidth(classNumber).replace(/[^0-9A-Za-z]/g, '').toUpperCase().slice(0, 3);
}

/**
 * 4つの欄から保存の文字列を組み立てる。どれかが空なら空（必須の欄の知らせを出させる）。
 */
export function formatPlate(parts: PlateParts): string {
  const area = parts.area.trim();
  const classNumber = formatClassNumber(parts.classNumber);
  const kana = parts.kana.trim();
  const serial = formatSerial(parts.serial);
  if (!area || !classNumber || !kana || !serial) return '';
  return `${area} ${classNumber} ${kana} ${serial}`;
}

// 地名（数字を含まない） 分類番号 ひらがな 一連番号。区切りの空白は無くても読む（「品川330な3859」）。
const PLATE_PATTERN = /^\s*([^\s\d０-９]+?)\s*([0-9０-９][0-9A-Za-z０-９Ａ-Ｚａ-ｚ]{0,2})\s*([ぁ-ゖ])\s*([0-9０-９・･\-－\s]+?)\s*$/;

/**
 * 保存の文字列を4つの欄に分ける。分けられない値は null（呼び出し側は元の文字列のまま残す）。
 */
export function parsePlate(value: string | null | undefined): PlateParts | null {
  if (!value) return null;
  const match = PLATE_PATTERN.exec(value);
  if (!match) return null;
  const digits = serialDigits(match[4]);
  if (digits.length < 1 || digits.length > 4) return null;
  // 欄には数字だけを出す（欄は4桁まで。表記の「38-59」「・・12」は組み立てるときに作る）。
  return {
    area: match[1],
    classNumber: formatClassNumber(match[2]),
    kana: match[3],
    serial: digits,
  };
}
